import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import BrandState from '../../components/BrandState'
import { type MouseEvent, useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import { useIsMobile } from '@/hooks/useResponsive'
import {
  type RolePermissionValue,
  type SettingRole,
  type SettingRolePayload,
  useCreateSettingRoleMutation,
  useDeleteSettingRoleMutation,
  useGetSettingRolesQuery,
  useUpdateSettingRoleMutation,
} from '../../store/api/settingsApi'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import SettingsRoleDialog from './SettingsRoleDialog'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '../../utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type ColumnKind = 'text' | 'date' | 'datetime' | 'permission'
type ColumnDef = {
  key: keyof SettingRole
  label: string
  kind?: ColumnKind
  filter?: ColumnFilterKind
}

const WIDTHS_TABLE_KEY = 'settings.role'

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'roleCodeUper', label: 'Role Code', filter: 'checkboxes' },
  { key: 'roleName', label: 'Role Name' },
  { key: 'description', label: 'Description' },
  { key: 'purchase', label: 'Purchase', kind: 'permission', filter: 'checkboxes' },
  { key: 'procurement', label: 'Procurement', kind: 'permission', filter: 'checkboxes' },
  { key: 'saleContract', label: 'Sales Contract', kind: 'permission', filter: 'checkboxes' },
  { key: 'saleControl', label: 'Sale Control', kind: 'permission', filter: 'checkboxes' },
  { key: 'setting', label: 'Setting', kind: 'permission', filter: 'checkboxes' },
  { key: 'virtualWarehouse', label: 'Virtual Warehouse', kind: 'permission', filter: 'checkboxes' },
  { key: 'futures', label: 'Futures', kind: 'permission', filter: 'checkboxes' },
  { key: 'position', label: 'Report', kind: 'permission', filter: 'checkboxes' },
  { key: 'quality', label: 'Quality', kind: 'permission', filter: 'checkboxes' },
  { key: 'masterData', label: 'Master Data', kind: 'permission', filter: 'checkboxes' },
  { key: 'factory', label: 'Factory control', kind: 'permission', filter: 'checkboxes' },
  { key: 'factoryBl', label: 'Bảo Lộc factory', kind: 'permission', filter: 'checkboxes' },
  { key: 'factoryNc', label: 'Nguyên Cường factory', kind: 'permission', filter: 'checkboxes' },
  { key: 'factoryNs', label: 'Nông Sản factory', kind: 'permission', filter: 'checkboxes' },
  { key: 'factoryNt', label: 'Nhơn Trạch factory', kind: 'permission', filter: 'checkboxes' },
  { key: 'factoryPaco', label: 'Pacorini factory', kind: 'permission', filter: 'checkboxes' },
  { key: 'matchingPs', label: 'Matching P-S', kind: 'permission', filter: 'checkboxes' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person', filter: 'checkboxes' },
]

const actionToolbarBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const permissionText = (value: RolePermissionValue | null | undefined): string => {
  if (value === 0) return 'View'
  if (value === 1) return 'Edit'
  return 'No access'
}

const cellValue = (row: SettingRole, col: ColumnDef): string => {
  const value = row[col.key]
  if (value == null) return ''
  if (col.kind === 'date') return formatAccessDate(value)
  if (col.kind === 'datetime') return formatAccessDateTime(value)
  if (col.kind === 'permission') return permissionText(value as RolePermissionValue)
  return String(value)
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save role. Please try again.'
  if (!('data' in error)) return 'Failed to save role. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save role. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save role. Please try again.'
}

const emptyDeleteDialogState = {
  open: false,
  error: '',
}

export default function SettingsRoleListView() {
  const isMobile = useIsMobile()
  const { canEdit } = usePagePermission()
  const {
    selectedId: selectedRoleCodeUper,
    setSelectedId: setSelectedRoleCodeUper,
    toggleSelect: toggleSelectRole,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths(WIDTHS_TABLE_KEY)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SettingRole> | null>(null)
  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [roleDialogMode, setRoleDialogMode] = useState<'new' | 'edit'>('new')
  const [deleteDialog, setDeleteDialog] = useState(emptyDeleteDialogState)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SettingRole
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'settings',
    prefKey: 'columns',
    subKey: 'roles',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: WIDTHS_TABLE_KEY,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data: rolesData, isLoading, isFetching, error } = useGetSettingRolesQuery()
  const [createRole, { isLoading: isCreating }] = useCreateSettingRoleMutation()
  const [updateRole, { isLoading: isUpdating }] = useUpdateSettingRoleMutation()
  const [deleteRole, { isLoading: isDeleting }] = useDeleteSettingRoleMutation()

  const baseRows = useMemo(() => rolesData?.items ?? [], [rolesData])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS, (row, _key, column) => cellValue(row, column as ColumnDef)),
    [filteredRows, sortState],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const selectedRow = useMemo(
    () => baseRows.find((row) => row.roleCodeUper === selectedRoleCodeUper) ?? null,
    [baseRows, selectedRoleCodeUper],
  )

  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const hasSelection = Boolean(selectedRow)
  const currentDialogRole = selectedRow

  const toggleSort = (key: keyof SettingRole) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  useEffect(() => {
    if (selectedRoleCodeUper && !baseRows.some((row) => row.roleCodeUper === selectedRoleCodeUper)) {
      setSelectedRoleCodeUper(null)
    }
  }, [baseRows, selectedRoleCodeUper])

  const handleOpenRoleDialog = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) return
    setRoleDialogMode(mode)
    setRoleDialogOpen(true)
  }

  const handleOpenDeleteDialog = () => {
    if (!selectedRow) return
    setDeleteDialog({ open: true, error: '' })
  }

  const handleSaveRole = async (payload: SettingRolePayload) => {
    if (roleDialogMode === 'new') {
      const saved = await createRole(payload).unwrap()
      setSelectedRoleCodeUper(saved.roleCodeUper)
      return
    }

    if (!selectedRow) {
      throw new Error('Please choose a record!')
    }

    const saved = await updateRole({ roleCodeUper: selectedRow.roleCodeUper, body: payload }).unwrap()
    setSelectedRoleCodeUper(saved.roleCodeUper)
  }

  const handleConfirmDelete = async () => {
    if (!selectedRow) return

    setDeleteDialog((prev) => ({ ...prev, error: '' }))
    try {
      await deleteRole(selectedRow.roleCodeUper).unwrap()
      setSelectedRoleCodeUper(null)
      setDeleteDialog(emptyDeleteDialogState)
    } catch (errorValue) {
      setDeleteDialog((prev) => ({
        ...prev,
        error: extractApiErrorMessage(errorValue),
      }))
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => handleOpenRoleDialog('new')}>
              New
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!hasSelection || !canEdit}
              onClick={() => handleOpenRoleDialog('edit')}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!hasSelection || !canEdit}
              onClick={handleOpenDeleteDialog}
            >
              Delete
            </Button>
          </Stack>
        }
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e: MouseEvent<HTMLElement>) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
        >
          Columns
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={() => filterPopover.close()}
        maxWidth={320}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Role code, name, description..."
            label="Search roles"
            onClear={() => filterPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={closeColPopover}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(String(colPopover.key), v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            minHeight: 30,
            py: 0.125,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Roles"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Roles found" description="Try adjusting the search above." />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const w = getWidth(col.key as string)
                  return (
                    <TableCell
                      key={col.key as string}
                      align="left"
                      className="app-table-header-cell"
                      onClick={() => toggleSort(col.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(col.key as string, e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: SettingRole) =>
              visibleColumns.map((col) => {
                const value = cellValue(row, col)
                const w = getWidth(col.key as string)
                return (
                  <TableCell
                    key={col.key as string}
                    align="left"
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Box
                      component="span"
                      className="app-table-cell-text"
                      title={value || '-'}
                      sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {value || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              /* eslint-disable @typescript-eslint/no-explicit-any */
              Table: (props: any) => (
                <Table {...props} size="small" stickyHeader sx={{ minWidth: Math.max(960, visibleColumns.length * 120), width: '100%' }} />
              ),
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowCode = Number.isFinite(idx) ? rows[idx]?.roleCodeUper : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowCode != null && rowCode === selectedRoleCodeUper}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowCode != null) toggleSelectRole(rowCode)
                    }}
                  />
                )
              },
              /* eslint-enable @typescript-eslint/no-explicit-any */
            }}
          />
          )}
        </Box>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <SettingsRoleDialog
        open={roleDialogOpen}
        mode={roleDialogMode}
        initialRole={currentDialogRole}
        onClose={() => setRoleDialogOpen(false)}
        onSubmit={handleSaveRole}
        isSubmitting={isCreating || isUpdating}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog(emptyDeleteDialogState)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: 'min(900px, 92vh)',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            flexWrap: 'nowrap',
          }}
        >
          <Typography
            component="span"
            noWrap
            title="Delete Role"
            sx={{
              flex: '1 1 auto',
              fontWeight: 700,
              fontSize: '1rem',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Delete Role
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
            <Button variant="outlined" size="small" onClick={() => setDeleteDialog(emptyDeleteDialogState)} sx={actionToolbarBtnSx} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" size="small" color="error" onClick={handleConfirmDelete} sx={actionToolbarBtnSx} disabled={isDeleting}>
              {isDeleting ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
            </Button>
          </Box>
        </DialogTitle>
        <Divider sx={{ flexShrink: 0 }} />
        <DialogContent
          dividers
          sx={{
            pt: 0.75,
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
            '& .MuiTypography-body2': { fontSize: '0.82rem' },
          }}
        >
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {deleteDialog.error ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {deleteDialog.error}
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Do you want to delete Role code = '{selectedRow?.roleCodeUper ?? ''}' ?
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
